import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AUTH_QUERY_KEY_ROOT } from '@/api/auth/constants/AUTH_QUERY_KEY_ROOT';
import { TAuthData, TSessionData } from '@/api/auth/types';
import { HIREOLOGY_APP_URL } from '@/constants/api';
import {
  isSameSessionIdentity,
  SessionIdentityChangedError,
} from '@/features/auth/utils/sessionIdentity';

/**
 * Whether the session cookie now belongs to somebody other than this tab's user.
 *
 * Deliberately not `fetchSessionData`: open tabs run this on every focus, and that
 * endpoint builds the whole session payload and touches the access token. This one
 * returns a single id.
 *
 * Decision tree:
 * - a different user -> changed
 * - no longer signed in -> changed, so the caller can go through the full check
 * - request failed -> unchanged, rather than signing someone out on a network blip
 */
export const hasSessionIdentityChanged = async (): Promise<boolean> => {
  try {
    const response = await fetch(
      `${HIREOLOGY_APP_URL}/users/session_identity`,
      { credentials: 'same-origin' }
    );

    // A server error is a blip, not a changed session. Reporting it as changed sends
    // the caller into session_data, whose own failure path navigates to sign-in — so
    // one bad minute during a deploy would sign out every tab as it is focused.
    if (response.status >= 500) return false;

    // An expired session lands on the sign-in page, which answers 200 with HTML.
    const contentType = response.headers.get('content-type') || '';
    if (!response.ok || !contentType.includes('application/json')) return true;

    const { userGlobalId } = await response.json();
    return !isSameSessionIdentity(userGlobalId);
  } catch {
    return false;
  }
};

export const fetchSessionData = async (redirect = true): Promise<TAuthData> => {
  // We are using fetch here to throw our own error responses.
  // Axios will return 200 status instead of the expected 302 if the user isn't logged in.
  // https://github.com/axios/axios/issues/5092
  // fetch follows 302 redirects automatically, so an unauthenticated request ends up at
  // the sign-in page (200 HTML). We detect that by catching the JSON parse failure and
  // redirecting rather than surfacing a confusing SyntaxError.
  try {
    const response = await fetch(`${HIREOLOGY_APP_URL}/users/session_data`);
    if (!response.ok) {
      if (redirect)
        window.location.assign(`${HIREOLOGY_APP_URL}/users/sign_in`);
      throw new Error(`${response.status} ${response.statusText}`);
    }
    let user: TSessionData;
    try {
      user = <TSessionData>await response.json();
    } catch {
      if (redirect)
        window.location.assign(`${HIREOLOGY_APP_URL}/users/sign_in`);
      throw new Error('Session endpoint returned non-JSON response');
    }
    const isSameIdentity = isSameSessionIdentity(user.currentUser.globalID);

    // The Rails session cookie is shared by every tab, so this response may
    // describe whoever signed in most recently rather than this tab's user.
    // Sending the tab to the logged-out page beats silently switching accounts.
    if (!isSameIdentity) {
      // replace, not assign: this fires on a page reached with Back, and pushing a
      // new entry would leave that page in history to be walked onto again.
      window.location.replace(
        `${HIREOLOGY_APP_URL}/users/logout/complete?reason=session_changed`
      );
      throw new SessionIdentityChangedError();
    }
    return { user };
  } catch (error) {
    // A changed identity is a deliberate redirect, not a failed request.
    if (!(error instanceof SessionIdentityChangedError)) {
      console.error('Error fetching session data:', error);
    }
    throw error;
  }
};

export function useAuthPrefetch({ skip }: { skip?: boolean } = {}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (skip) return;
    const prefetch = async () => {
      await queryClient.prefetchQuery({
        queryKey: [AUTH_QUERY_KEY_ROOT, HIREOLOGY_APP_URL],
        queryFn: () => fetchSessionData(false),
        retry: 0,
        staleTime: Infinity,
      });
    };
    prefetch();
  }, [queryClient, skip]);
}

const useAuth = () => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const query = useQuery({
    queryKey: [AUTH_QUERY_KEY_ROOT, HIREOLOGY_APP_URL],
    queryFn: () => fetchSessionData(),
    retry: 0,
    staleTime: Infinity,
    meta: {
      errorCallback: (error: unknown) => {
        // A changed identity has already started a navigation, so this is a
        // redirect rather than a failure — without the first clause the tab paints
        // the server-error page for the whole round trip to Rails, since a freshly
        // booted document has no cached session to fall back on.
        if (
          error instanceof SessionIdentityChangedError ||
          (error instanceof Error && /\d/.test(error.message))
        ) {
          setIsRedirecting(true);
        }
      },
    },
  });

  const {
    data,
    error,
    isLoading,
    isError,
    refetch,
    isRefetching,
    isPending,
    isSuccess,
  } = query;

  return {
    data,
    error,
    isLoading,
    isError,
    refetch,
    isRefetching,
    isRedirecting,
    isPending,
    isSuccess,
  };
};

export default useAuth;
